import {
  IMPROVEMENTS_MAP_CARD_CLASS_NAME,
  IMPROVEMENTS_MAP_ICON_ID_PREFIX,
  IMPROVEMENTS_MAP_MASK,
  IMPROVEMENTS_MAP_MAX_CLIENT_HEIGHT,
  IMPROVEMENTS_MAP_MAX_CLIENT_WIDTH,
  IMPROVEMENTS_MAP_PADDING,
  IMPROVEMENTS_MAP_TOP_EDGE,
  IMPROVEMENTS_MAP_X_GAP_MAX,
  IMPROVEMENTS_MAP_X_GAP_MIN,
  IMPROVEMENTS_MAP_Y_GAP_MAX,
  IMPROVEMENTS_MAP_Y_GAP_MIN,
} from './ImprovementsMap.constants';
import {DI_TOKENS} from '@/di/di.tokens';
import {ImprovementForUI} from '@/stores/ImprovementsStore/ImprovementsStore.types';
import {Point, Rectangle} from 'pixi.js';

import {injected} from 'brandi';

import {GCSEngine} from '@specials/gcs-engine';

import {HTMLElementId} from '@/constants';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {ImprovementsStore} from '@/stores/ImprovementsStore/ImprovementsStore';
import {GlobalWindow} from '@/types';
import {HammerJS} from '@/utils/HammerJS';
import {NumberUtils} from '@/utils/NumberUtils';
import {PseudoRandom} from '@/utils/random/PseudoRandom';

export interface ImprovementsMapStage {
  cardPositionList: Point[];
  stageWidth: number;
  stageHeight: number;
}

export type ImprovementsMapLinesConfig = {
  position: Point;
  length: number;
  angleInRad: number;
  progress: number;
}[];

enum ImprovementsMapMaskItem {
  Empty = 0,
  Card = 1,
}

export type ImprovementsMapMask = ImprovementsMapMaskItem[][];

export class ImprovementsMapService {
  private stagePosition: Point;
  private hm: HammerManager | null;
  private stageNode: HTMLElement | null;
  private panBounds: Rectangle;
  private config: {
    xDelta: number;
    yDelta: number;
    xGap: number;
    yGap: number;
    seed: number;
  };

  constructor(
    private readonly window: GlobalWindow,
    private readonly random: PseudoRandom,
    private readonly clickerStateStore: ClickerStateStore,
  ) {
    this.stagePosition = new Point();
    this.hm = null;
    this.panBounds = new Rectangle();
    this.config = {
      xDelta: 35,
      yDelta: 70,
      xGap: 0.7,
      yGap: 0.9,
      seed: 387,
    };
  }

  public generateImprovementsStage(improvements: ImprovementForUI[]): ImprovementsMapStage {
    this.stageNode = this.window.document.getElementById(HTMLElementId.ImprovementsMapStage);

    // генерация позиций карточек улучшений
    const cardPositionList = this.generateImprovementsPositionList(improvements.length);

    // находим максимальную ширину и высоту из карточек, чтобы последняя полностью карточка влезла в вьюпорт
    const cardMaxSizes = this.getCardMaxSize();

    const stageSizes = this.getStageSizes(cardPositionList, cardMaxSizes);

    return {
      cardPositionList,
      stageWidth: stageSizes.x,
      stageHeight: stageSizes.y,
    };
  }

  public generateLinesBetweenImprovements(improvements: ImprovementForUI[]): ImprovementsMapLinesConfig {
    const result: ImprovementsMapLinesConfig = [];

    for (let i = 1; i < improvements.length; i++) {
      const currentCardId = improvements[i].id;
      const prevCardId = improvements[i - 1].id;

      // линиями соединяются именно иконки-звездочки карточек, поэтому собираем их
      const iconNodeCurrent = this.getCardIconNode(currentCardId);
      const iconNodePrev = this.getCardIconNode(prevCardId);

      if (!iconNodeCurrent) throw new Error(`Не найдена карточка для улучшения ${currentCardId}`);
      if (!iconNodePrev) throw new Error(`Не найдена карточка для улучшения ${prevCardId}`);
      if (!this.stageNode) throw new Error('Контейнер для карты улучшений не найден');

      const {x: x1, y: y1} = iconNodePrev.getBoundingClientRect();
      const {x: x2, y: y2} = iconNodeCurrent.getBoundingClientRect();
      const {x: stageX, y: stageY} = this.stageNode.getBoundingClientRect();

      const {width: iconPrevCardWidth, height: iconPrevCardHeight} = iconNodePrev.getBoundingClientRect();
      const iconPrevCardHalfWidth = iconPrevCardWidth / 2;
      const iconPrevCardHalfHeight = iconPrevCardHeight / 2;

      const {width: iconCurrentCardWidth, height: iconCurrentCardHeight} = iconNodeCurrent.getBoundingClientRect();
      const iconCurrentCardHalfWidth = iconCurrentCardWidth / 2;
      const iconCurrentCardHalfHeight = iconCurrentCardHeight / 2;

      // расчет центра иконки относительно координат сцены на странице
      const startLinePoint = new Point(x1 - stageX + iconPrevCardHalfWidth, y1 - stageY + iconPrevCardHalfHeight);

      const endLinePoint = new Point(x2 - stageX + iconCurrentCardHalfWidth, y2 - stageY + iconCurrentCardHalfHeight);

      const length = GCSEngine.Vector2Utils.distance(startLinePoint, endLinePoint);

      const angle = GCSEngine.Vector2Utils.getAngleBetweenVectors(startLinePoint, endLinePoint);

      result.push({
        length,
        angleInRad: angle,
        position: startLinePoint,
        progress: this.getProgressBetweenNeighboringImprovements(improvements[i - 1], improvements[i]),
      });
    }

    return result;
  }

  public updateProgress(
    improvements: ImprovementForUI[],
    linesConfig: ImprovementsMapLinesConfig,
  ): ImprovementsMapLinesConfig {
    const result: ImprovementsMapLinesConfig = [];

    for (let i = 1; i < improvements.length; i++) {
      result.push({
        ...linesConfig[i - 1],
        progress: this.getProgressBetweenNeighboringImprovements(improvements[i - 1], improvements[i]),
      });
    }

    return result;
  }

  public setStagePosition(improvements: ImprovementForUI[]): void {
    const container = this.getContainer();

    if (!this.stageNode || !container) return;

    const delta = new Point();

    for (let i = 0; i < improvements.length; i++) {
      if (ImprovementsStore.isImprovementStatusBought(improvements[i])) {
        const iconNode = this.getCardIconNode(improvements[i].id);
        if (!iconNode) return;

        const x = iconNode.getBoundingClientRect().x;
        const stageX = this.stageNode.getBoundingClientRect().x;

        const iconPrevCardHalfWidth = iconNode.clientWidth / 2;

        const relativeNodePosition = x - stageX + iconPrevCardHalfWidth;

        const containerWidth = container.getBoundingClientRect().width;

        // двигаем карту только если нода находится вне вьюпорта
        if (relativeNodePosition > containerWidth) {
          delta.x = -(relativeNodePosition - containerWidth / 2);
        }
      }
    }

    const position = this.getNewStagePosition(delta.x, delta.y);
    this.stageNode.style.transform = `translate(${position.x}px, ${position.y}px)`;
    this.stagePosition = position.clone();
  }

  public initHammer(): void {
    if (this.hm) return;

    this.panBounds = this.getPanBounds();

    void HammerJS.getInstance()
      .getModule()
      .then((HammerModule) => {
        if (!this.stageNode) return;

        this.hm = new HammerModule.Manager(this.stageNode);
        this.hm.add([new HammerModule.Pan()]);

        this.hm.on('panstart', this.panStart);
        this.hm.on('panmove', this.handlePanMove);
        this.hm.on('panend pancancel', this.handlePanEnd);
      });
  }

  public destroy(): void {
    if (this.hm) {
      this.hm.off('panstart', this.panStart);
      this.hm.off('panmove', this.handlePanMove);
      this.hm.off('panend pancancel', this.handlePanEnd);
    }

    this.hm = null;
    this.stagePosition = new Point();
    this.panBounds = new Rectangle();
  }

  private panStart = (e: HammerInput): void => {
    e.preventDefault();

    if (!this.stageNode) return;

    this.stageNode.style.cursor = 'grabbing';
    this.stageNode.style.pointerEvents = 'none';
  };

  private handlePanMove = (e: HammerInput): void => {
    e.preventDefault();

    if (!this.stageNode) return;

    const {x, y} = this.getNewStagePosition(e.deltaX, e.deltaY);

    this.stageNode.style.transform = `translate(${x}px, ${y}px)`;
  };

  private handlePanEnd = (e: HammerInput): void => {
    e.preventDefault();

    if (!this.stageNode) return;

    this.stageNode.style.cursor = 'grab';
    this.stageNode.style.pointerEvents = '';

    this.stagePosition = this.getNewStagePosition(e.deltaX, e.deltaY);
  };

  private getNewStagePosition(dx: number, dy: number): Point {
    const posX = NumberUtils.clamp(-this.panBounds.width, this.panBounds.x, this.stagePosition.x + dx);
    const posY = NumberUtils.clamp(-this.panBounds.height, this.panBounds.y, this.stagePosition.y + dy);

    return new Point(posX, posY);
  }

  private getPanBounds(): Rectangle {
    const container = this.getContainer();

    if (!this.stageNode || !container) return new Rectangle();

    const {width: containerWidth, bottom: bottomY} = container.getBoundingClientRect();
    const {width: stageWidth, bottom: stageY} = this.stageNode.getBoundingClientRect();

    return new Rectangle(0, 0, stageWidth - containerWidth, Math.abs(bottomY - stageY));
  }

  private getContainer(): HTMLElement | null {
    return this.window.document.getElementById(HTMLElementId.ClickerGamePageContainer);
  }

  private getProgressBetweenNeighboringImprovements(
    prevImprovement: ImprovementForUI,
    currentImprovement: ImprovementForUI,
  ): number {
    if (ImprovementsStore.isImprovementStatusLock(currentImprovement)) {
      // оба залочены, между ними нет прогресса
      if (ImprovementsStore.isImprovementStatusLock(prevImprovement)) {
        return 0;
      }

      return NumberUtils.calcPercentage(
        this.clickerStateStore.getCoinsPerHourMine(),
        currentImprovement.requiredTotalIncomePerHour,
      );
    }

    return 100;
  }

  private getXGap(): number {
    const xGap = this.config.xGap;
    const clientWidth = this.window.document.documentElement.clientWidth;

    return (
      NumberUtils.lerp(
        IMPROVEMENTS_MAP_X_GAP_MIN,
        IMPROVEMENTS_MAP_X_GAP_MAX,
        Math.min(1, clientWidth / IMPROVEMENTS_MAP_MAX_CLIENT_WIDTH),
      ) * xGap
    );
  }

  private getYGap(): number {
    const yGap = this.config.yGap;
    const clientHeight = this.window.document.documentElement.clientHeight;

    return (
      NumberUtils.lerp(
        IMPROVEMENTS_MAP_Y_GAP_MIN,
        IMPROVEMENTS_MAP_Y_GAP_MAX,
        Math.min(1, clientHeight / IMPROVEMENTS_MAP_MAX_CLIENT_HEIGHT),
      ) * yGap
    );
  }

  private getDelta(min: number, max: number): number {
    return this.random.generateIntegerNumber(min, max);
  }

  private getCardIconNode(id: string): HTMLElement | null {
    return this.window.document.getElementById(IMPROVEMENTS_MAP_ICON_ID_PREFIX + id);
  }

  private getCardMaxSize(): Point {
    // собираем ноды карточек, она должны быть уже отрендерены
    const cardNodes = Array.from(this.window.document.querySelectorAll(`.${IMPROVEMENTS_MAP_CARD_CLASS_NAME}`));

    return cardNodes.reduce((result, card) => {
      const {height, width} = card.getBoundingClientRect();
      result.x = Math.max(result.x, width);
      result.y = Math.max(result.y, height);
      return result;
    }, new Point(-Infinity, -Infinity));
  }

  private getStageSizes(cardPositionList: Point[], cardSizes: Point): Point {
    return cardPositionList.reduce((result, position) => {
      result.x = Math.max(result.x, position.x + cardSizes.x + IMPROVEMENTS_MAP_PADDING * 2);
      result.y = Math.max(result.y, position.y + cardSizes.y + IMPROVEMENTS_MAP_PADDING * 2);
      return result;
    }, new Point(-Infinity, -Infinity));
  }

  private generateImprovementsPositionList(count: number): Point[] {
    if (count === 0) return [];
    // сброс рандома
    this.random.init(this.config.seed);

    const positionList: Point[] = this.getCardPositionsByMask(IMPROVEMENTS_MAP_MASK, count);

    // у первой карточки постоянная позиция
    positionList[0].x = IMPROVEMENTS_MAP_PADDING;
    positionList[0].y = IMPROVEMENTS_MAP_PADDING;

    return positionList;
  }

  private getCardPositionsByMask(mask: number[][], positionCount: number): Point[] {
    if (mask.length === 0) return [];

    const {xDelta, yDelta} = this.config;

    const xGap = this.getXGap();
    const yGap = this.getYGap();

    const positionList: Point[] = [];
    let columnIndex = 0;
    let isFillPositionList = true;
    const maskColumns = mask[0].length;

    while (isFillPositionList) {
      for (let rowIndex = 0; rowIndex < mask.length; rowIndex++) {
        if (mask[rowIndex][columnIndex % maskColumns] === ImprovementsMapMaskItem.Card) {
          // вариация псевдорандомной дельтой, чтобы карточки не были на одном уровне
          const pos = new Point(
            positionList.length * xGap + this.getDelta(-xDelta, xDelta),
            Math.max(IMPROVEMENTS_MAP_TOP_EDGE, rowIndex * yGap + this.getDelta(-yDelta, yDelta)),
          );

          positionList.push(pos);

          if (positionCount === positionList.length) {
            isFillPositionList = false;
            break;
          }
        }
      }

      columnIndex++;
    }

    return positionList;
  }
}

injected(ImprovementsMapService, DI_TOKENS.globalWindow, DI_TOKENS.pseudoRandom, DI_TOKENS.clickerStateStore);
